import { observer } from "mobx-react-lite"
import React, { useEffect, useLayoutEffect, useCallback, useMemo, useRef, useState } from "react"
import { Platform } from "~/ui/primitives"
import { useTheme } from '~/ui/styled'
import { Loading } from "~widgets/loading"
import { useAudioPlayer } from "../../context"
import { Message, useStores } from "../../models"
import { resolveChatById } from "~hooks/useActiveChat"
import { ColumnFlex, Text } from "~components/El"
import { debug, getMediaPath, globalToast } from "~utils"
import MessageListItem from "./message-list-item"
import { limit, queryMessagesByChatIdentity } from "../../services/sqlite"
import { MessageProps, PlayerState } from "../message/message.props"
import styled from '~/ui/styled'
import { ViewMode } from "~components/chat/view-mode"

interface MessageListProps {
  setMode: (mode: ViewMode) => void
  setPopupOptionListModal?: ({ visible, message }: { visible: boolean; message: Message }) => void
  /** 必须传入 URL 中的 chat_id，禁止依赖 chatStore.current */
  activeChatId: string
  onScroll?: (offsetY: number) => void
  onScrollBeginDrag?: () => void
  onScrollEndDrag?: () => void
  scrollById?: (id: number) => void
  listRef: React.RefObject<any>
  visibleContentPosition?: boolean
  expandToIndex?: number | null
  /** 从搜索/外链进入时需定位到的消息 id 或 message_id */
  scrollToMessageId?: string | number | null
}

const INITIAL_RENDER_COUNT = 40
const RENDER_BATCH_SIZE = 30
/** 首进房间本地不足一页时，最多向服务端补拉几轮更旧历史 */
const INITIAL_FILL_ROUNDS = 3

const ANDROID_START_PLAYER_TIMEOUT_MS = 25_000

function raceWithTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error(`[AudioPlayer] ${label} timeout`)), ms),
    ),
  ])
}

const LIST_CONTENT_CONTAINER_STYLE = {
  flexGrow: 1,
  justifyContent: "flex-end" as const,
  paddingBottom: 10,
}

// 消息列表组件
const MessageList: React.FC<MessageListProps> = ({
  setMode,
  setPopupOptionListModal,
  onScroll,
  onScrollBeginDrag,
  onScrollEndDrag,
  listRef,
  visibleContentPosition,
  expandToIndex,
  scrollToMessageId,
  activeChatId,
}) => {
  const theme = useTheme()
  const rootStore = useStores()
  const { appState: state, chatStore, websocketStore } = rootStore
  const chat = resolveChatById(chatStore, activeChatId)
  const playStatus = useRef<Message & PlayerState & { resetPlayer: () => void }>()
  const data = chat?.sortedMessages ?? []
  const [renderCount, setRenderCount] = useState(INITIAL_RENDER_COUNT)
  const [internalScrollIndex, setInternalScrollIndex] = useState<number | null>(null)
  const scrollTargetIndex = expandToIndex ?? internalScrollIndex
  const hasScrollTarget =
    scrollToMessageId != null && scrollToMessageId !== "" || scrollTargetIndex != null

  const matchMessageId = useCallback((message: Message, id: string | number) => {
    const target = String(id)
    return String(message.id) === target || String(message.message_id) === target
  }, [])

  const listData = useMemo(
    () => data.slice(0, Math.min(Math.max(renderCount, INITIAL_RENDER_COUNT), data.length)),
    [data, renderCount],
  )

  const fetchDataSeqRef = useRef(0)
  /** 当前会话已完成一次自动首屏拉取（防 WS ready 变化触发 effect 死循环） */
  const autoLoadedChatRef = useRef<string | null>(null)
  /** 用户是否在最新消息处（inverted offsetY ≈ 0） */
  const isAtBottomRef = useRef(true)
  const prevNewestMsgIdRef = useRef<string | undefined>(undefined)

  const canLoadOlderRef = useRef(false)

  /**
   * 首进房间时异步补全消息；inverted 列表易停在错误 offset。
   * 仅在「首屏锚定阶段」内滚到最新，避免上拉加载历史后把用户拽回底部。
   */
  const pendingInitialAnchorRef = useRef(true)

  useLayoutEffect(() => {
    pendingInitialAnchorRef.current = true
    autoLoadedChatRef.current = null
    isAtBottomRef.current = true
    prevNewestMsgIdRef.current = undefined
    canLoadOlderRef.current = false
    setRenderCount(INITIAL_RENDER_COUNT)
    setInternalScrollIndex(null)
    fetchDataSeqRef.current += 1
    const activeChat = resolveChatById(chatStore, activeChatId)
    activeChat?.setIsSearching(false)
    // 上次会话可能误设 isNoMoreData，消息不足一页时重进允许再拉
    if (activeChat && activeChat.sortedMessages.length < limit) {
      activeChat.setIsNoMoreData(false)
    }
    activeChat?.relinkMessagesFromStore()
    const t = window.setTimeout(() => {
      canLoadOlderRef.current = true
    }, 400)
    return () => {
      window.clearTimeout(t)
      fetchDataSeqRef.current += 1
      pendingInitialAnchorRef.current = false
      canLoadOlderRef.current = false
    }
  }, [activeChatId, chatStore])

  useLayoutEffect(() => {
    if (scrollToMessageId == null || scrollToMessageId === "") return
    pendingInitialAnchorRef.current = false
  }, [scrollToMessageId, activeChatId])

  useEffect(() => {
    if (scrollToMessageId == null || scrollToMessageId === "") return undefined

    let cancelled = false
    let retryTimer: ReturnType<typeof setTimeout> | null = null
    let retryCount = 0
    const maxHydrateRetries = 12

    const resolveTargetIndex = async (activeChat: NonNullable<ReturnType<typeof resolveChatById>>) => {
      pendingInitialAnchorRef.current = false

      let index = activeChat.sortedMessages.findIndex((el) => matchMessageId(el, scrollToMessageId))
      if (index >= 0) return index

      if (activeChat.isNoMoreData || activeChat.sortedMessages.length === 0) return -1

      let attempts = 0
      while (index < 0 && !activeChat.isNoMoreData && attempts < 30 && !cancelled) {
        const oldest = activeChat.sortedMessages[activeChat.sortedMessages.length - 1]
        await activeChat.publicReadChatHistory(oldest?.id ?? null)
        index = activeChat.sortedMessages.findIndex((el) => matchMessageId(el, scrollToMessageId))
        attempts += 1
      }
      return index
    }

    const attemptScroll = async () => {
      const activeChat = resolveChatById(chatStore, activeChatId)
      if (!activeChat || cancelled) return

      const index = await resolveTargetIndex(activeChat)
      if (cancelled) return

      if (index < 0) {
        if (retryCount < maxHydrateRetries) {
          retryCount += 1
          retryTimer = setTimeout(() => {
            void attemptScroll()
          }, 250)
          return
        }
        globalToast.show("未找到该消息")
        setInternalScrollIndex(null)
        return
      }

      setInternalScrollIndex(index)
      setRenderCount((prev) => Math.max(prev, index + 5))
      isAtBottomRef.current = index === 0
    }

    void attemptScroll()

    return () => {
      cancelled = true
      if (retryTimer != null) clearTimeout(retryTimer)
    }
  }, [scrollToMessageId, activeChatId, chatStore, matchMessageId, data.length])

  useEffect(() => {
    if (scrollTargetIndex == null || scrollTargetIndex < 0) return
    pendingInitialAnchorRef.current = false
    setRenderCount((prev) => Math.max(prev, scrollTargetIndex + 5))
  }, [scrollTargetIndex])

  useLayoutEffect(() => {
    if (scrollTargetIndex == null || scrollTargetIndex < 0) return
    if (listData.length <= scrollTargetIndex) return

    let cancelled = false
    let tries = 0
    const maxTries = 48

    const scrollToTarget = () => {
      if (cancelled || tries >= maxTries) return
      tries += 1
      const list = listRef.current
      if (!list) {
        requestAnimationFrame(scrollToTarget)
        return
      }
      const ok = list.scrollToIndex?.({ index: scrollTargetIndex, animated: tries > 2 })
      if (ok) return
      requestAnimationFrame(scrollToTarget)
    }

    requestAnimationFrame(() => requestAnimationFrame(scrollToTarget))
    return () => {
      cancelled = true
    }
  }, [scrollTargetIndex, listData.length, listRef])

  useEffect(() => {
    if (!chat || chat.isSearching) return
    setRenderCount((prev) => {
      if (data.length <= prev) return prev
      return Math.min(data.length, prev + RENDER_BATCH_SIZE)
    })
  }, [data.length, chat?.isSearching])

  const scrollListToNewest = useCallback(() => {
    const list = listRef.current
    if (!list || listData.length === 0) return

    let tries = 0
    const maxTries = 4
    const go = () => {
      if (tries >= maxTries) return
      tries += 1
      try {
        list.scrollToOffset({ offset: 0, animated: false })
      } catch (_) {
        try {
          list.scrollToIndex({ index: 0, animated: false })
        } catch (_) {
          /* 首帧尚未量完子项 */
        }
      }
      if (tries < maxTries) requestAnimationFrame(go)
    }
    requestAnimationFrame(go)
  }, [listData.length, listRef])

  useLayoutEffect(() => {
    if (!chat || !pendingInitialAnchorRef.current) return
    if (hasScrollTarget) return
    if (chat.isNoMoreData && data.length === 0) {
      pendingInitialAnchorRef.current = false
      return
    }
    if (listData.length === 0 || chat.isSearching) return
    scrollListToNewest()
    const initialLoadDone = autoLoadedChatRef.current === activeChatId
    if (data.length >= limit || (chat.isNoMoreData && initialLoadDone)) {
      pendingInitialAnchorRef.current = false
    }
  }, [listData.length, data.length, chat?.isSearching, chat?.isNoMoreData, activeChatId, hasScrollTarget, scrollListToNewest])

  // 停留在底部时收到新消息，自动滚到最新
  useLayoutEffect(() => {
    if (!chat || pendingInitialAnchorRef.current) return

    const newestId = listData[0]?.message_id
    if (!newestId) return

    const prevNewest = prevNewestMsgIdRef.current
    if (prevNewest != null && newestId !== prevNewest && isAtBottomRef.current) {
      scrollListToNewest()
    }
    prevNewestMsgIdRef.current = newestId
  }, [listData, chat, scrollListToNewest])

  /** 首进房间：本地补齐 → 服务端最新一屏 → 不足一页再向更旧翻 */
  const runInitialHistoryLoad = useCallback(async () => {
    const activeChat = resolveChatById(chatStore, activeChatId)
    if (!activeChat || activeChat.isNoMoreData) return
    if (autoLoadedChatRef.current === activeChatId) return
    if (!websocketStore.ready || activeChat.isSearching) return

    autoLoadedChatRef.current = activeChatId

    if (!activeChat.isGameGroup && activeChat.sortedMessages.length >= limit) {
      setRenderCount((prev) => Math.max(prev, activeChat.sortedMessages.length))
      return
    }

    const seq = ++fetchDataSeqRef.current
    activeChat.setIsSearching(true)

    try {
      if (activeChat.isGameGroup) {
        await activeChat.publicReadChatHistory(null, { replace: true, manageSearching: false })
        if (seq !== fetchDataSeqRef.current) return
        setRenderCount((prev) => Math.max(prev, activeChat.sortedMessages.length))
        return
      }

      let total = activeChat.sortedMessages.length

      // 内存不足一页时先从本地库灌入最新一屏
      if (total < limit) {
        const localBatch = await queryMessagesByChatIdentity(activeChat, 0)
        if (seq !== fetchDataSeqRef.current) return
        if (localBatch.length > 0) {
          activeChat.prependMessages(localBatch)
          total = activeChat.sortedMessages.length
        }
      }

      // 本地缓存不完整时先拉服务端「最新一屏」，避免只往更旧翻漏掉新消息
      if (total < limit) {
        await activeChat.publicReadChatHistory(null, { manageSearching: false })
        if (seq !== fetchDataSeqRef.current) return
        total = activeChat.sortedMessages.length
      }

      // 仍不足一页则向更旧补拉（有上限，防止弱网死循环）
      let rounds = 0
      while (total < limit && !activeChat.isNoMoreData && rounds < INITIAL_FILL_ROUNDS) {
        const oldest = activeChat.sortedMessages[activeChat.sortedMessages.length - 1]
        const prevTotal = total
        await activeChat.publicReadChatHistory(oldest?.id ?? null, { manageSearching: false })
        if (seq !== fetchDataSeqRef.current) return
        total = activeChat.sortedMessages.length
        if (total === prevTotal) break
        rounds += 1
      }

      setRenderCount((prev) => Math.max(prev + RENDER_BATCH_SIZE, total))
    } catch {
      if (activeChat.sortedMessages.length === 0) {
        activeChat.setIsNoMoreData(true)
      }
    } finally {
      if (seq === fetchDataSeqRef.current) {
        activeChat.setIsSearching(false)
        if (pendingInitialAnchorRef.current && !hasScrollTarget) {
          scrollListToNewest()
        }
      }
    }
  }, [activeChatId, chatStore, hasScrollTarget, scrollListToNewest, websocketStore.ready])

  /** 上拉加载更旧历史：先扩渲染窗口 → 本地分页 → 服务端分页 */
  const fetchOlderHistory = useCallback(async () => {
    if (!chat || chat.isNoMoreData || chat.isSearching) return false
    if (!canLoadOlderRef.current) return false

    pendingInitialAnchorRef.current = false

    if (renderCount < data.length) {
      setRenderCount((prev) => Math.min(data.length, prev + RENDER_BATCH_SIZE))
      return true
    }

    const seq = ++fetchDataSeqRef.current
    chat.setIsSearching(true)
    const beforeCount = chat.sortedMessages.length

    try {
      if (!chat.isGameGroup) {
        const localBatch = await queryMessagesByChatIdentity(chat, data.length)
        if (seq !== fetchDataSeqRef.current) return false
        if (localBatch.length > 0) {
          chat.prependMessages(localBatch)
          setRenderCount((prev) =>
            Math.min(chat.sortedMessages.length, prev + RENDER_BATCH_SIZE),
          )
          if (localBatch.length >= limit) return true
        }
      }

      if (websocketStore.ready) {
        const oldest = chat.sortedMessages[chat.sortedMessages.length - 1]
        await chat.publicReadChatHistory(oldest?.id ?? null, { manageSearching: false })
      } else if (chat.sortedMessages.length === 0) {
        chat.setIsNoMoreData(true)
      }

      if (seq !== fetchDataSeqRef.current) return false

      if (chat.sortedMessages.length === beforeCount && chat.sortedMessages.length > 0) {
        // 本地/服务端均无新增，标记没有更多
        if (!chat.isNoMoreData) chat.setIsNoMoreData(true)
      }

      setRenderCount((prev) => Math.max(prev + RENDER_BATCH_SIZE, chat.sortedMessages.length))
    } catch {
      if (chat.sortedMessages.length === 0) {
        chat.setIsNoMoreData(true)
      }
    } finally {
      if (seq === fetchDataSeqRef.current) {
        chat.setIsSearching(false)
      }
    }

    return true
  }, [chat, data.length, renderCount, websocketStore.ready])

  const loadOlderMessages = useCallback(() => {
    if (!canLoadOlderRef.current) return
    if (pendingInitialAnchorRef.current) return
    if (chat?.isSearching) return
    void fetchOlderHistory()
  }, [chat?.isSearching, fetchOlderHistory])

  const audioPlayer = useAudioPlayer()

  // 重置语音播放器状态
  const onAudioReset = useCallback(async () => {
    await audioPlayer.stopPlayer()
    audioPlayer.removePlayBackListener()
    debug("[AudioPlayer] reseted")
  }, [])

  // 播放语音消息
  const onAudioPlay = useCallback<MessageProps["onAudioPlay"]>(
    async (message, state, resetPlayer, playBack?) => {
      const newPlayStatus = {
        resetPlayer,
        ...state,
        ...message,
      } as Message & PlayerState & { resetPlayer: () => void }
      const notSameMessage = playStatus.current?.message_id !== newPlayStatus.message_id
      const httpUrl = getMediaPath(newPlayStatus)
      const voiceDurationMs = Math.max(
        newPlayStatus.length ?? 0,
        newPlayStatus.attach?.length ?? 0,
      )
      // ① 切换播放消息时，清理旧监听 + 停止 player
      if (playStatus.current && notSameMessage) {
        await onAudioReset()
        playStatus.current.resetPlayer()
      }
      if (!httpUrl || voiceDurationMs <= 0) {
        throw new Error("[AudioPlayer] missing media url or voice duration")
      }

      // ② 开始播放新的语音（安卓上部分异常 URL 会让 startPlayer 长时间不 resolve，需超时）
      try {
        const startPromise = audioPlayer.startPlayer(httpUrl)
        await (Platform.OS === "android"
          ? raceWithTimeout(startPromise, ANDROID_START_PLAYER_TIMEOUT_MS, "startPlayer")
          : startPromise)
      } catch (e) {
        await audioPlayer.stopPlayer().catch(() => undefined)
        audioPlayer.removePlayBackListener()
        newPlayStatus.resetPlayer()
        throw e
      }

      // 跳转到进度（用于恢复功能）
      if (newPlayStatus.currentPos) {
        await audioPlayer.seekToPlayer(newPlayStatus.currentPos)
      }

      // ③ 统一注册播放回调（播放进度、播放完成）
      let playbackEnded = false
      const endPlayback = () => {
        if (playbackEnded) return
        playbackEnded = true
        void onAudioStop()
      }

      audioPlayer.addPlayBackListener((e) => {
        // 外部自定义回调（比如控制波形动画）
        playBack && playBack(e)
        if (playbackEnded) return

        // duration 为 0/NaN 时，若仍用 currentPosition >= duration 会在安卓上每 100ms 触发结束逻辑 → 卡死
        if (e.isFinished === true) {
          endPlayback()
          return
        }

      })

      playStatus.current = newPlayStatus
      debug(`[AudioPlayer] play → ${httpUrl}`)
    },
    [],
  )

  // 停止播放语音消息
  const onAudioStop = useCallback(async () => {
    await audioPlayer.stopPlayer()
    audioPlayer.removePlayBackListener()

    if (playStatus.current) {
      playStatus.current.resetPlayer()
      playStatus.current = undefined
    }

    debug("[AudioPlayer] stopped")
  }, [])


  // 暂停播放语音消息
  const onAudioPause = useCallback(async () => {
    await audioPlayer.pausePlayer()
    debug("[AudioPlayer] paused")
  }, [])

  // 恢复播放语音消息
  const onAudioResume = useCallback(async () => {
    await audioPlayer.resumePlayer()
    debug("[AudioPlayer] resumed")
  }, [])

  // 渲染消息
  const renderMessage = useCallback(
    ({ item }) => {
      return (
        <MessageListItem
          setMode={setMode}
          setPopupOptionListModal={setPopupOptionListModal}
          message={item}
          onAudioPlay={onAudioPlay}
          onAudioPause={onAudioPause}
          onAudioStop={onAudioStop}
          onAudioResume={onAudioResume}
          chatTypeEnum={chat?.chatTypeEnum}
        />
      )
    },
    [
      setMode,
      setPopupOptionListModal,
      chat?.chatTypeEnum,
      onAudioPlay,
      onAudioPause,
      onAudioStop,
      onAudioResume,
    ],
  )

  const handleMarkAllRead = useCallback(() => {
    chat?.markAllRead()
  }, [chat])

  const viewabilityConfig = useMemo(
    () => ({
      itemVisiblePercentThreshold: 10,
      minimumViewTime: 80,
    }),
    [],
  )


  const handleScroll = useCallback((e) => {
    const y = e.nativeEvent.contentOffset.y
    isAtBottomRef.current = y <= 12
    onScroll?.(y)
  }, [onScroll])

  const historyListHeader = useMemo(() => {
    if (listData.length === 0) return null
    if (chat?.isSearching) {
      return (
        <HistoryHeader>
          <Loading size={18} color={theme.colors.primary} />
          <Text fontSize={12} color={theme.colors.secondary} mt="6px">
            加载历史消息...
          </Text>
        </HistoryHeader>
      )
    }
    if (chat?.isNoMoreData) {
      return (
        <HistoryHeader>
          <Text fontSize={12} color={theme.colors.secondaryText}>
            — 没有更多消息了 —
          </Text>
        </HistoryHeader>
      )
    }
    return null
  }, [chat?.isSearching, chat?.isNoMoreData, listData.length, theme.colors.primary, theme.colors.secondary, theme.colors.secondaryText])

  // 进入会话且 WS 就绪后自动拉首屏一次（本地 → 服务端最新 → 不足一页再向更旧补）
  useEffect(() => {
    void runInitialHistoryLoad()
  }, [runInitialHistoryLoad])

  // 停留在聊天页时，未读数变化就标记已读。
  // 除新消息(data.length)外，游戏群的 GAME_CHAT_UNREAD_SUMMARY 只改 packedUnreadHint、不改 data.length，
  // 这里把未读数也纳入依赖，避免右上角/列表仍残留未读角标。
  useLayoutEffect(() => {
    handleMarkAllRead()
  }, [data.length, chat?.messageUnreadCount, handleMarkAllRead])

  useLayoutEffect(() => {
    if (state.match(/inactive|background/)) {
      onAudioStop()
    }
    return () => {
      onAudioStop()
    }
  }, [state])

  if (!chat) return null

  return (
    <Container>
      {chat.isSearching && !chat.isNoMoreData && listData.length === 0 && (
        <Area>
          <Loading size={ 20} color={theme.colors.primary} />
        </Area>
      )}
      <List
        key={activeChatId}
        refreshing={false}
        ref={listRef}
        contentContainerStyle={LIST_CONTENT_CONTAINER_STYLE}
        showsVerticalScrollIndicator={false}
        scrollEventThrottle={16}
        onScroll={handleScroll}
        onScrollBeginDrag={onScrollBeginDrag}
        onScrollEndDrag={onScrollEndDrag}
        data={listData}
        extraData={`${activeChatId}:${listData.length}`}
        keyExtractor={(item, index) =>
          item.message_id
            ? `msg-${activeChatId}-${item.message_id}`
            : `msg-${activeChatId}-fallback-${index}`
        }
        renderItem={renderMessage}
        onEndReached={loadOlderMessages}
        onEndReachedThreshold={0.25}
        ListHeaderComponent={historyListHeader}
        viewabilityConfig={viewabilityConfig}
        inverted={true}
        suppressInitialInvertedScroll={hasScrollTarget}
        maintainVisibleContentPosition={ visibleContentPosition ? {} : { minIndexForVisible: 0, autoscrollToTopThreshold: 20 }}
        updateCellsBatchingPeriod={50}
        removeClippedSubviews={false}
      />
    </Container>
  )
}

const Container = styled.div`
  flex: 1;
  min-height: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  background-color: ${({ theme }) => theme.colors.background};
`
const Area = styled.div`
  z-index: 3333;
  position: absolute;
  width: 100%;
  height: 50px;
  display: flex;
  justify-content: center;
  align-items: center;
`
const HistoryHeader = styled(ColumnFlex)`
  width: 100%;
  height: auto;
  padding: 12px 0 8px;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`
const List = styled.FlatList`
  flex: 1;
  min-height: 0;
  height: 100%;
  background-color: ${({ theme }) => theme.colors.background};
`

export default observer(MessageList)
